import { memo } from 'react';
import { Flex, styled } from '@cad-arc';
import { useEffect } from 'react';
import { IconButtonControl } from './styled';
import { usePrevious } from '../../common/hooks/use-previous';
import { colors } from '../../colors/colors';
import { Tooltip } from '~/common/components/tooltip';

const MapControlsWrapper = styled(Flex)`
    bottom: 20px;
    right: 12px;
    z-index: 1; /* needed to get above map */
    position: absolute;
    flex-direction: column;
    justify-content: center;
    width: 1px;
    margin-right: 31px;

    & > button:not(:last-child) {
        margin-bottom: 11;
    }
`;

const ControlButtonGroup = styled('div')`
    margin: 10px 0;

    & > button:not(:last-child) {
        border-bottom: 0;
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
    }

    & > button:not(:first-of-type) {
        border-top-right-radius: 0;
        border-top-left-radius: 0;
    }
`;

const IconButtonControlWithBackgroundColor = styled(IconButtonControl)<{
    $isFollowModeEnabled: boolean;
}>`
    background-color: ${(props) =>
        props.$isFollowModeEnabled ? 'var(--arc-colors-brand-hover)' : colors.mapButtonBackground};
`;

type MapControlsProps = {
    zoomForUpdate?: number;
    focusEntity: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    resetMap: () => void;
    setZoomLevel: (zoomLevel?: number) => void;
    isFollowModeEnabled: boolean;
};
export const MapControls = memo(
    ({
        zoomForUpdate,
        zoomIn,
        zoomOut,
        resetMap,
        setZoomLevel,
        focusEntity,
        isFollowModeEnabled,
    }: MapControlsProps) => {
        const previousZoomForUpdate = usePrevious(zoomForUpdate);
        useEffect(
            function updateZoomLevel() {
                if (zoomForUpdate !== undefined && previousZoomForUpdate !== zoomForUpdate) {
                    setZoomLevel(zoomForUpdate);
                }
            },
            [previousZoomForUpdate, setZoomLevel, zoomForUpdate]
        );

        return (
            <MapControlsWrapper>
                <Tooltip overlay="Focus" mouseEnterDelay={0.5}>
                    <IconButtonControlWithBackgroundColor
                        data-testid="MAP_FOCUS_BUTTON"
                        size="md"
                        icon="Crosshairs"
                        aria-label="Focus"
                        onClick={focusEntity}
                        $isFollowModeEnabled={isFollowModeEnabled}
                    />
                </Tooltip>
                <ControlButtonGroup>
                    <Tooltip
                        overlay={<span style={{ whiteSpace: 'nowrap' }}>Zoom In</span>}
                        mouseEnterDelay={0.5}
                    >
                        <IconButtonControl
                            data-testid="MAP_ZOOM_IN_BUTTON"
                            size="md"
                            icon="Add"
                            aria-label="Zoom In"
                            onClick={zoomIn}
                        />
                    </Tooltip>
                    <Tooltip
                        overlay={<span style={{ whiteSpace: 'nowrap' }}>Zoom Out</span>}
                        mouseEnterDelay={0.5}
                    >
                        <IconButtonControl
                            data-testid="MAP_ZOOM_OUT_BUTTON"
                            size="md"
                            icon="Close"
                            aria-label="Zoom Out"
                            onClick={zoomOut}
                        />
                    </Tooltip>
                </ControlButtonGroup>
                <Tooltip overlay="Reset" mouseEnterDelay={0.5}>
                    <IconButtonControl
                        data-testid="MAP_RESET_MAP_BUTTON"
                        size="md"
                        icon="Map"
                        aria-label="Reset"
                        onClick={resetMap}
                    />
                </Tooltip>
            </MapControlsWrapper>
        );
    }
);
